<template>
  <!-- On mobile, just show a button that navigates to /notifications -->
  <v-btn
    v-if="isMobile"
    icon
    variant="text"
    class="notification-trigger"
    @click="toggleNotifications"
  >
    <v-badge :content="unreadCount" :model-value="unreadCount > 0" color="error" max="99">
      <v-icon
        :icon="unreadCount > 0 ? 'mdi-bell' : 'mdi-bell-outline'"
        :color="unreadCount > 0 ? 'primary' : undefined"
      />
    </v-badge>
  </v-btn>

  <!-- On desktop, show the dropdown menu -->
  <v-menu
    v-else
    v-model="isOpen"
    :close-on-content-click="false"
    location="bottom end"
    width="400"
    max-height="600"
    offset="8"
    :z-index="3100"
  >
    <template #activator="{ props: menuProps }">
      <v-btn
        v-bind="menuProps"
        icon
        variant="text"
        class="notification-trigger"
        @click="toggleNotifications"
      >
        <v-badge :content="unreadCount" :model-value="unreadCount > 0" color="error" max="99">
          <v-icon
            :icon="unreadCount > 0 ? 'mdi-bell' : 'mdi-bell-outline'"
            :color="unreadCount > 0 ? 'primary' : undefined"
          />
        </v-badge>
      </v-btn>
    </template>

    <v-card class="notification-panel">
      <v-card-title class="d-flex align-center justify-space-between pa-3 pb-2">
        <div class="text-h6">Notifications</div>
        <v-menu location="bottom end">
          <template #activator="{ props: menuProps }">
            <v-btn v-bind="menuProps" icon="mdi-dots-horizontal" variant="text" size="small" />
          </template>
          <v-list density="compact" min-width="200">
            <v-list-item :disabled="markingAllRead || unreadCount === 0" @click="markAllAsRead">
              <template #prepend>
                <v-icon icon="mdi-check-all" size="20" />
              </template>
              <v-list-item-title>Mark all as read</v-list-item-title>
            </v-list-item>
            <v-list-item @click="$router.push('/settings?section=notifications')">
              <template #prepend>
                <v-icon icon="mdi-cog-outline" size="20" />
              </template>
              <v-list-item-title>Notification settings</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </v-card-title>

      <v-tabs v-model="activeTab" density="compact" class="notification-tabs">
        <v-tab value="all">All</v-tab>
        <v-tab value="mentions">Mentions</v-tab>
      </v-tabs>

      <v-divider />

      <v-card-text class="pa-0" style="max-height: 500px; overflow-y: auto">
        <div v-if="isLoading" class="text-center pa-8">
          <v-progress-circular indeterminate color="primary" size="48" />
          <div class="text-caption mt-4">Loading notifications...</div>
        </div>

        <div v-else-if="filteredNotifications.length === 0" class="empty-state pa-6 text-center">
          <div class="empty-state__icon mb-3">
            <svg
              width="64"
              height="64"
              viewBox="0 0 80 80"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M40 8C41.5 8 42.7 9.2 42.7 10.7V13.5C51.3 15 58 22.5 58 31.5V45L64 55H16L22 45V31.5C22 22.5 28.7 15 37.3 13.5V10.7C37.3 9.2 38.5 8 40 8Z"
                stroke="rgba(var(--v-theme-on-surface), 0.3)"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                fill="none"
              />
              <path
                d="M32 55C32 59.4 35.6 63 40 63C44.4 63 48 59.4 48 55"
                stroke="rgba(var(--v-theme-on-surface), 0.3)"
                stroke-width="2"
                stroke-linecap="round"
                fill="none"
              />
            </svg>
          </div>
          <div class="text-body-2 text-medium-emphasis">{{ emptyStateMessage }}</div>
        </div>

        <div v-else class="notification-list">
          <NotificationItem
            v-for="notification in filteredNotifications"
            :key="notification.id"
            :notification="notification"
            @click="handleNotificationClick"
            @mark-read="markAsRead"
            @remove="removeNotification"
          />
        </div>

        <!-- Note: "Load more" button removed from dropdown because it only shows 10 notifications.
             Users should click "View all" to see more notifications on the full page. -->
      </v-card-text>

      <v-card-actions v-if="notifications.length > 0" class="pa-3 pt-2">
        <v-spacer />
        <v-btn variant="text" size="small" @click="viewAllNotifications">View all</v-btn>
      </v-card-actions>
    </v-card>
  </v-menu>

  <!-- Appeal Modal -->
  <AppealModal
    v-model="showAppealModal"
    :post-id="appealPostId"
    :post-title="appealPostTitle"
    :post-body="appealPostBody"
    :violation-type="appealViolationType"
    :content-type="appealContentType"
    @submitted="handleAppealSubmitted"
  />
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useDisplay } from 'vuetify';
import {
  useInfiniteNotifications,
  useUnreadCount,
  useMarkNotificationAsRead,
  useMarkAllNotificationsAsRead,
  useDeleteNotification,
} from '@/composables/useNotificationQueries';
import { useSdk } from '@/composables/useSdk';
import NotificationItem from './NotificationItem.vue';
import AppealModal from '@/components/modals/AppealModal.vue';
import type { Notification } from '@/types/notification';

const router = useRouter();
const { mobile: isMobile } = useDisplay();
const { sdk } = useSdk();
const isOpen = ref(false);
const activeTab = ref('all');
const markingAllRead = ref(false);

// Appeal modal state
const showAppealModal = ref(false);
const appealPostId = ref('');
const appealPostTitle = ref('');
const appealPostBody = ref('');
const appealViolationType = ref('');
const appealContentType = ref('post');

// Set to true to use mock data for testing UI
const USE_MOCK_DATA = false;

function generateMockNotifications(): Notification[] {
  return [
    {
      id: "1",
      notificationId: "1",
      type: "song_upload_succeeded",
      notificationType: "song_upload_succeeded",
      title: "Song Approved",
      message: 'Your song "My Mind (Live)" has been successfully uploaded!',
      read: false,
      createdAt: new Date(Date.now() - 1000 * 60 * 5),
      postId: "song-1",
      postTitle: "My Mind (Live)",
      postType: "SONG",
      postImage: "https://picsum.photos/seed/song1/200",
      data: {
        actorName: "EPonGO",
        actorUsername: "epongo_official",
        postId: "song-1",
        postType: "SONG",
        artist: "Leanora Landron",
      },
    },
    {
      id: "2",
      notificationId: "2",
      type: "like",
      notificationType: "post_liked",
      title: "New Reactions",
      message: 'Adrian Tyler, Courtney Osteen, Dana Andognini and 2 other people reacted to your song "Bad Bad Feeling".',
      read: false,
      createdAt: new Date(Date.now() - 1000 * 60 * 21),
      postId: "song-2",
      postTitle: "Bad Bad Feeling",
      postType: "SONG",
      postImage: "https://picsum.photos/seed/song2/200",
      data: {
        actorName: "EPonGO",
        actorUsername: "epongo_official",
        postId: "song-2",
        postType: "SONG",
        artist: "Leanora Landron",
      },
    },
    {
      id: "3",
      notificationId: "3",
      type: "song_upload_succeeded",
      notificationType: "song_upload_succeeded",
      title: "Song Approved",
      message: 'Your song "Bad Bad Feeling" has been successfully uploaded!',
      read: true,
      createdAt: new Date(Date.now() - 1000 * 60 * 45),
      readAt: new Date(),
      postId: "song-2",
      postTitle: "Bad Bad Feeling",
      postType: "SONG",
      postImage: "https://picsum.photos/seed/song2/200",
      data: {
        actorName: "EPonGO",
        actorUsername: "epongo_official",
        postId: "song-2",
        postType: "SONG",
        artist: "Leanora Landron",
      },
    },
    {
      id: "4",
      notificationId: "4",
      type: "follow",
      notificationType: "user_followed",
      title: "New Follower",
      message: "Jane Smith started following you",
      read: true,
      createdAt: new Date(Date.now() - 1000 * 60 * 30),
      readAt: new Date(),
      data: {
        actorName: "Jane Smith",
        actorUsername: "janesmith",
      },
    },
    {
      id: "5",
      notificationId: "5",
      type: "comment",
      notificationType: "post_commented",
      title: "New Comment",
      message: "Fan Account commented on your post",
      read: false,
      createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24),
      data: {
        actorName: "Fan Account",
        actorUsername: "fan123",
        postContent: "Totally agree with this!",
      },
    },
  ];
}

// Cache for user profiles and post images
const userProfileCache = ref<Record<string, { avatar?: string; displayName?: string }>>({});
const postImageCache = ref<Record<string, string>>({});

// Local enhanced notifications with avatar/image data applied
const enhancedNotifications = ref<Notification[]>([]);

const { notifications: rawNotifications, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage, refetch } =
  useInfiniteNotifications();

// Use enhanced notifications for display
const notifications = enhancedNotifications;

/**
 * Fetch user profiles for notifications that have fromUserId but no avatar
 */
async function fetchUserProfiles(notifs: Notification[]): Promise<void> {
  const userIds = [
    ...new Set(
      notifs
        .filter((n) => n.fromUserId && !userProfileCache.value[n.fromUserId])
        .map((n) => n.fromUserId!),
    ),
  ];

  if (userIds.length === 0 || !sdk) return;

  try {
    // Fetch profiles individually using the SDK's fetchUserProfileById
    const profilePromises = userIds.map((userId) =>
      sdk.fetchUserProfileById(userId).catch((err) => {
        console.warn(`[NotificationCenter] Failed to fetch profile for ${userId}:`, err);
        return null;
      }),
    );
    const profiles = await Promise.all(profilePromises);

    // Cache the profiles and update notification objects
    profiles.forEach((profile: unknown, index: number) => {
      const userId = userIds[index];
      if (profile && userId) {
        const p = profile as Record<string, unknown>;
        const cachedData = {
          avatar: (p.avatar || p.avatarUrl) as string | undefined,
          displayName: (p.name || p.displayName) as string | undefined,
        };
        userProfileCache.value[userId] = cachedData;
      }
    });

    // Trigger reactivity by updating notifications array
    applyProfileDataToNotifications(notifs);
  } catch (error) {
    console.error('[NotificationCenter] Failed to fetch user profiles:', error);
  }
}

/**
 * Apply cached profile data to notifications
 */
function applyProfileDataToNotifications(notifs: Notification[]): void {
  for (const n of notifs) {
    if (n.fromUserId && userProfileCache.value[n.fromUserId]) {
      const cached = userProfileCache.value[n.fromUserId];
      if (cached.avatar && !n.fromUserAvatar) {
        n.fromUserAvatar = cached.avatar;
        if (n.data) {
          n.data.actorAvatar = cached.avatar;
        }
      }
      if (cached.displayName && !n.fromUserName) {
        n.fromUserName = cached.displayName;
        if (n.data) {
          n.data.actorName = cached.displayName;
        }
      }
    }
  }
}

/**
 * Fetch post images for song-related notifications
 */
async function fetchPostImages(notifs: Notification[]): Promise<void> {
  const postIds = [
    ...new Set(
      notifs
        .filter((n) => {
          const isSongNotification =
            n.type === 'song_upload_succeeded' ||
            n.notificationType === 'song_upload_succeeded' ||
            n.postType === 'SONG' ||
            (n.type === 'rating' && n.postType === 'SONG');
          return isSongNotification && n.postId && !n.postImage && !postImageCache.value[n.postId];
        })
        .map((n) => n.postId!),
    ),
  ];

  if (postIds.length === 0 || !sdk) return;

  try {
    // Fetch posts individually using SDK's getPostByUlid
    const postPromises = postIds.map((postId) =>
      sdk.getPostByUlid(postId).catch((err) => {
        console.warn(`[NotificationCenter] Failed to fetch post ${postId}:`, err);
        return null;
      }),
    );
    const posts = await Promise.all(postPromises);

    // Cache the post images
    posts.forEach((post, index) => {
      const postId = postIds[index];
      if (post && postId) {
        const postData = post as Record<string, unknown>;
        const images = postData.images as Array<{ url?: string }> | undefined;
        let image: string | undefined;

        if (images && images.length > 0) {
          image = images[0]?.url;
        } else if (postData.coverImage) {
          image = postData.coverImage as string;
        } else if (postData.image) {
          image = postData.image as string;
        }

        if (image) {
          postImageCache.value[postId] = image;
        }
      }
    });

    // Apply cached post images to notifications
    applyPostImagesToNotifications(notifs);
  } catch (error) {
    console.error('[NotificationCenter] Failed to fetch post images:', error);
  }
}

/**
 * Apply cached post images to notifications
 */
function applyPostImagesToNotifications(notifs: Notification[]): void {
  for (const n of notifs) {
    if (n.postId && postImageCache.value[n.postId]) {
      const image = postImageCache.value[n.postId];
      if (!n.postImage) {
        n.postImage = image;
        if (n.data) {
          n.data.postImage = image;
        }
      }
    }
  }
}

// Watch for raw notifications changes and create enhanced copies
watch(
  rawNotifications,
  async (newNotifications) => {
    // Use mock data for testing
    if (USE_MOCK_DATA) {
      enhancedNotifications.value = generateMockNotifications();
      return;
    }

    if (newNotifications.length > 0) {
      // Create deep copies to avoid mutating TanStack Query cache
      const copies = newNotifications.map(n => ({ ...n, data: { ...n.data } }));

      // Apply any cached data first
      applyProfileDataToNotifications(copies);
      applyPostImagesToNotifications(copies);

      // Update enhanced notifications (triggers reactivity)
      enhancedNotifications.value = copies;

      // Fetch missing profiles and images
      await fetchUserProfiles(copies);
      await fetchPostImages(copies);

      // Re-apply after fetch and update
      applyProfileDataToNotifications(copies);
      applyPostImagesToNotifications(copies);

      // Trigger reactivity with new array
      enhancedNotifications.value = [...copies];
    } else {
      enhancedNotifications.value = [];
    }
  },
  { immediate: true }
);

const { unreadCount: apiUnreadCount } = useUnreadCount();
const markAsReadMutation = useMarkNotificationAsRead();
const markAllMutation = useMarkAllNotificationsAsRead();
const deleteMutation = useDeleteNotification();

// Use local unread count for mock data
const unreadCount = computed(() => {
  if (USE_MOCK_DATA) {
    return enhancedNotifications.value.filter(n => !n.read).length;
  }
  return apiUnreadCount.value;
});

const filteredNotifications = computed(() => {
  const allNotifications = notifications.value;
  console.log('[NotificationCenter] All notifications:', allNotifications.length, allNotifications);
  console.log('[NotificationCenter] Active tab:', activeTab.value);

  switch (activeTab.value) {
    case 'mentions':
      return allNotifications.filter((n) => n.type === 'mention').slice(0, 10);
    default:
      // "all" tab - show all notifications (limit to 10 in dropdown)
      return allNotifications.slice(0, 10);
  }
});

const emptyStateIcon = computed(() => {
  switch (activeTab.value) {
    case 'mentions':
      return 'mdi-at';
    default:
      return 'mdi-bell-off-outline';
  }
});

const emptyStateTitle = computed(() => {
  switch (activeTab.value) {
    case 'mentions':
      return 'No mentions yet';
    default:
      return 'No notifications yet';
  }
});

const emptyStateMessage = computed(() => {
  switch (activeTab.value) {
    case 'mentions':
      return "You'll see mentions here when people @ you.";
    default:
      return "When you get notifications, they'll show up here";
  }
});

function toggleNotifications(): void {
  // On mobile, navigate directly to /notifications instead of showing the dropdown
  if (isMobile.value) {
    router.push('/notifications');
    return;
  }

  if (!isOpen.value) {
    refetch();
  }
}

async function markAsRead(notificationId: string): Promise<void> {
  markAsReadMutation.mutate(notificationId);
}

async function markAllAsRead(): Promise<void> {
  if (notifications.value.length === 0) return;

  markingAllRead.value = true;

  // For mock data, update local state
  if (USE_MOCK_DATA) {
    enhancedNotifications.value = enhancedNotifications.value.map(n => ({
      ...n,
      read: true,
      readAt: new Date(),
    }));
    markingAllRead.value = false;
    return;
  }

  const lastNotificationId = notifications.value[0]?.id || '';

  markAllMutation.mutate(lastNotificationId, {
    onSettled: () => {
      markingAllRead.value = false;
    },
  });
}

async function removeNotification(notificationId: string): Promise<void> {
  deleteMutation.mutate(notificationId);
}

function handleNotificationClick(notification: Notification): void {
  if (!notification.read) {
    markAsRead(notification.id);
  }

  // Handle group invite - navigate to group
  if (notification.type === 'group_invite' && notification.data.group_ulid) {
    router.push(`/group/${notification.data.group_ulid}`);
    isOpen.value = false;
    return;
  }

  // Handle follow notifications - navigate to the follower's profile
  if (notification.type === 'follow') {
    const username = notification.fromUserUsername || notification.data.actorUsername;
    if (username) {
      router.push(`/profile/${username}`);
      isOpen.value = false;
      return;
    }
  }

  // Handle content_removed notifications - open appeal modal
  if (notification.type === 'content_removed') {
    appealPostId.value = notification.data.content_ulid || notification.data.postId || '';
    appealPostTitle.value = notification.data.post_title || notification.postTitle || '';
    appealPostBody.value = notification.data.post_body || '';
    appealViolationType.value = notification.data.violation_type || notification.data.reason || '';
    appealContentType.value = notification.data.content_type || 'post';
    showAppealModal.value = true;
    isOpen.value = false;
    return;
  }

  if (notification.data.postId) {
    // Route to /song/:ulid for song-related notifications
    const postType = notification.postType || notification.data.postType;
    const isSongNotification = notification.type === 'song_upload_succeeded' || postType === 'SONG';
    const route = isSongNotification ? `/song/${notification.data.postId}` : `/post/${notification.data.postId}`;
    router.push(route);
    isOpen.value = false;
  }
}

function handleAppealSubmitted(postId: string): void {
  console.log('Appeal submitted for post:', postId);
}

function viewAllNotifications(): void {
  router.push('/notifications');
  isOpen.value = false;
}

watch(activeTab, () => {
  // Could refetch with specific type filter if needed
});
</script>

<style scoped>
/* Trigger button */
.notification-trigger {
  position: relative;
  transition: transform 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.notification-trigger:hover {
  transform: scale(1.05);
}

/* Panel container */
.notification-panel {
  background: rgb(var(--v-theme-surface));
  border: 1px solid rgba(var(--v-border-color), 0.6);
  box-shadow:
    0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 10px 20px -5px rgba(0, 0, 0, 0.08),
    0 20px 40px -10px rgba(0, 0, 0, 0.06);
  border-radius: 12px !important;
  overflow: hidden;
  backdrop-filter: blur(10px);
}

/* Header styling */
.v-card-title {
  font-weight: 700;
  font-size: 1.125rem;
  letter-spacing: -0.01em;
  border-bottom: 1px solid rgba(var(--v-border-color), 0.4);
}

/* Tabs */
.notification-tabs {
  border-bottom: 1px solid rgba(var(--v-border-color), 0.4);
  padding: 0 8px;
}

.notification-tabs :deep(.v-tab) {
  font-weight: 600;
  font-size: 0.875rem;
  letter-spacing: 0.01em;
  text-transform: none;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.notification-tabs :deep(.v-tab:hover) {
  background-color: rgba(var(--v-theme-on-surface), 0.04);
}

.notification-tabs :deep(.v-tab--selected) {
  color: rgb(var(--v-theme-primary));
}

/* Empty state */
.empty-state {
  max-width: 300px;
  margin: 0 auto;
  padding: 40px 20px;
  animation: fadeIn 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.empty-state .text-h6 {
  font-weight: 700;
  color: rgb(var(--v-theme-on-surface));
}

.empty-state .text-body-2 {
  color: rgba(var(--v-theme-on-surface), 0.6);
  line-height: 1.5;
}

/* Scrollbar */
.v-card-text::-webkit-scrollbar {
  width: 8px;
}

.v-card-text::-webkit-scrollbar-track {
  background: transparent;
}

.v-card-text::-webkit-scrollbar-thumb {
  background: rgba(var(--v-theme-on-surface), 0.2);
  border-radius: 4px;
  border: 2px solid transparent;
  background-clip: content-box;
}

.v-card-text::-webkit-scrollbar-thumb:hover {
  background: rgba(var(--v-theme-primary), 0.5);
  background-clip: content-box;
}

/* Load more section */
.text-center.pa-4 {
  border-top: 1px solid rgba(var(--v-border-color), 0.3);
}

/* Footer actions */
.v-card-actions {
  border-top: 1px solid rgba(var(--v-border-color), 0.4);
  background-color: rgba(var(--v-theme-surface-variant), 0.3);
}

.v-card-actions .v-btn {
  font-weight: 600;
  font-size: 0.875rem;
  text-transform: none;
  letter-spacing: 0.01em;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.v-card-actions .v-btn:hover {
  background-color: rgba(var(--v-theme-on-surface), 0.06);
  transform: translateY(-1px);
}

/* Loading state */
.v-progress-circular {
  animation: fadeIn 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Responsive */
@media (max-width: 600px) {
  .notification-panel {
    width: 350px !important;
    max-width: 90vw;
    border-radius: 8px !important;
  }

  .v-card-title {
    font-size: 1rem;
    padding: 12px 16px 8px !important;
  }

  .notification-tabs {
    padding: 0 4px;
  }

  .notification-tabs :deep(.v-tab) {
    font-size: 0.8125rem;
    min-width: 80px;
  }

  .empty-state {
    padding: 30px 16px;
  }

  .v-card-actions {
    padding: 12px 16px !important;
  }
}

/* Dark mode enhancements */
@media (prefers-color-scheme: dark) {
  .notification-panel {
    box-shadow:
      0 4px 6px -1px rgba(0, 0, 0, 0.3),
      0 10px 20px -5px rgba(0, 0, 0, 0.2),
      0 20px 40px -10px rgba(0, 0, 0, 0.15);
  }

  .v-card-actions {
    background-color: rgba(var(--v-theme-surface-variant), 0.2);
  }
}

/* Accessibility */
.notification-trigger:focus-visible {
  outline: 2px solid rgb(var(--v-theme-primary));
  outline-offset: 2px;
  border-radius: 50%;
}

/* Badge styling */
:deep(.v-badge__badge) {
  font-weight: 700;
  font-size: 0.6875rem;
  min-width: 18px;
  height: 18px;
  padding: 0 4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

/* Notification list */
.notification-list {
  background: transparent;
}

.notification-list :deep(.notification-item-wrapper) {
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Settings button hover */
.v-card-title .v-btn {
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.v-card-title .v-btn:hover {
  background-color: rgba(var(--v-theme-on-surface), 0.08);
  transform: rotate(90deg);
}
</style>
